/**
 * archivedActivitiesViewModel — `(reduxState, raw) → props` des activités archivées (patron B).
 *
 * `getArchivedActivities` n'a aucun cas SUCCESS dans un réducteur : la réponse reste dans
 * l'écran, exactement comme le legacy la garde dans son `useState`
 * (`ArchivedActivitiesScreen.js:43`, `:76-92`). `raw` est donc la liste accumulée page après
 * page, tenue par `useArchivedActivitiesData`.
 *
 * Le tri est celui du backend (`deleted_at desc`, `ActivityController::getArchivedActivities`) :
 * le legacy ne retrie rien côté client, et une archive se lit dans l'ordre où elle a été rangée.
 */
import { createSelector } from "reselect"

import { toUIActivities, type ApiActivity } from "../../../adapters/parent/Activities/activityAdapter"
import { type UIActivity } from "../../../ui/types"

export type ArchivedActivitiesViewModel = {
    activities: UIActivity[]
    /** Premier chargement — aucune page reçue et aucune erreur. */
    loading: boolean
    refreshing: boolean
    loadingMore: boolean
    error: unknown | null
}

/** Redux root is still untyped in this app — keep the VM boundary explicit. */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

export const archivedActivitiesViewModel = createSelector(
    [
        (state: ReduxState) => state?.data?.staticData?.school_subjects,
        (state: ReduxState) => state?.api?.getArchivedActivities,
        (_state: ReduxState, raw: ApiActivity[] | null) => raw,
    ],
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    (subjects: any, req: any, raw: ApiActivity[] | null): ArchivedActivitiesViewModel => {
        const requesting = !!req?.request
        const page = Number(req?.urlParams?.page ?? 1)
        const error = req?.error ?? null

        return {
            activities: toUIActivities(raw, subjects ?? []),
            // Jamais `req.request` : la tranche est mise au repos avant que le rendu suivant applique
            // `raw`, donc « fini » sans page se lirait comme « vide » (`ports/CLAUDE.md` § 1).
            loading: raw == null && error == null,
            refreshing: requesting && raw != null && page <= 1,
            loadingMore: requesting && page > 1,
            error: raw == null ? error : null,
        }
    }
)
